import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, RotateCw, Package } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Dispatcher dialog to open a kit for a driver.
 * - Pick driver, mode (delivery/restock), then add line items from inventory.
 * - "Repeat last kit" button copies the driver's most recent kit composition.
 */
export function OpenKitDialog({ open, onOpenChange, drivers = [], products = [], onOpened }) {
  const [driverId, setDriverId] = useState('');
  const [mode, setMode] = useState('delivery');
  const [rows, setRows] = useState([]); // [{product_id, qty}]
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  useEffect(() => {
    if (!open) {
      setDriverId(''); setMode('delivery'); setRows([]);
    }
  }, [open]);

  const productsById = useMemo(
    () => Object.fromEntries(products.map(p => [p.id, p])),
    [products]
  );

  const total = useMemo(
    () => rows.reduce((s, r) => {
      const p = productsById[r.product_id];
      return s + (p ? p.price * (Number(r.qty) || 0) : 0);
    }, 0),
    [rows, productsById]
  );

  const availableToAdd = useMemo(() => {
    const inOrder = new Set(rows.map(r => r.product_id));
    return products.filter(p => !inOrder.has(p.id) && p.stock > 0);
  }, [products, rows]);

  const addProduct = (pid) => {
    setRows(prev => [...prev, { product_id: pid, qty: 1 }]);
  };

  const setQty = (pid, qty) => {
    const num = Math.max(0, parseInt(qty, 10) || 0);
    const product = productsById[pid];
    if (product && num > product.stock) {
      toast.error(`Only ${product.stock} of ${product.name} in warehouse`);
      return;
    }
    setRows(prev => prev.map(r => r.product_id === pid ? { ...r, qty: num } : r));
  };

  const removeRow = (pid) => {
    setRows(prev => prev.filter(r => r.product_id !== pid));
  };

  const repeatLastKit = async () => {
    if (!driverId) {
      toast.error('Pick a driver first');
      return;
    }
    setLoadingTemplate(true);
    try {
      // Look up driver's most recent kit (any status) — just hit /kits/movements to find a recent open
      // Easier: hit /kits which returns active kits — if they have one, use it. Otherwise, fallback to all-time last kit lookup not available — show toast.
      // For Wave 1-Lite simplicity we look in the active kits list:
      const { data: activeKitsRes } = await axios.get(`${API_URL}/api/kits`, { withCredentials: true });
      const existing = (activeKitsRes.kits || []).find(k => k.driver_id === driverId);
      if (existing) {
        const next = existing.items
          .filter(i => i.qty_initial > 0)
          .map(i => ({ product_id: i.product_id, qty: i.qty_initial }));
        setRows(next);
        toast.success(`Loaded ${next.length} item(s) from current kit`);
      } else {
        toast.info('No prior kit on file for this driver — build manually');
      }
    } catch (err) {
      toast.error('Could not load template');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const submit = async () => {
    if (!driverId) { toast.error('Pick a driver'); return; }
    const valid = rows.filter(r => r.qty > 0);
    if (valid.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/kits/open`,
        {
          driver_id: driverId,
          mode,
          items: valid.map(r => ({ product_id: r.product_id, quantity: r.qty })),
        },
        { withCredentials: true }
      );
      toast.success('Kit opened');
      onOpened?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open kit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#050003] border border-[#39FF14] rounded-none text-[#A3C7A8] font-mono max-w-2xl"
        data-testid="open-kit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#39FF14] font-mono flex items-center gap-2">
            <Package className="w-5 h-5" />
            {'>'} OPEN_KIT
          </DialogTitle>
          <p className="text-[#4B6B52] text-xs">// Opening a kit closes any existing active kit for this driver.</p>
        </DialogHeader>

        {/* Driver + Mode pickers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2 space-y-1">
            <label className="text-[#6B8E73] text-xs uppercase">Driver</label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger
                className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] rounded-none font-mono"
                data-testid="kit-driver-select"
              >
                <SelectValue placeholder="Pick a driver…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                {drivers.map(d => (
                  <SelectItem key={d.id} value={d.id} className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">
                    <span className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${d.online ? 'bg-[#39FF14]' : 'bg-[#4B6B52]'}`} />
                      {d.name}
                      <span className="text-[#6B8E73] text-xs">· {d.mode || 'delivery'}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[#6B8E73] text-xs uppercase">Mode</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] rounded-none font-mono" data-testid="kit-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                <SelectItem value="delivery" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">DELIVERY</SelectItem>
                <SelectItem value="restock" className="text-[#00F0FF] font-mono focus:bg-[#2A0F3D]">RESTOCK</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Repeat last kit shortcut */}
        <div className="flex items-center justify-between border-t border-[#2A0F3D] pt-3">
          <span className="text-[#6B8E73] text-xs uppercase">Items</span>
          <Button
            variant="outline" size="sm"
            onClick={repeatLastKit} disabled={!driverId || loadingTemplate}
            className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none text-xs font-mono"
            data-testid="repeat-last-kit"
          >
            <RotateCw className="w-3 h-3 mr-1" />
            REPEAT LAST KIT
          </Button>
        </div>

        {/* Item rows */}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
          {rows.length === 0 ? (
            <p className="text-[#4B6B52] text-xs">// No items yet. Pick from below or use REPEAT LAST KIT.</p>
          ) : rows.map(r => {
            const p = productsById[r.product_id];
            if (!p) return null;
            return (
              <div key={r.product_id} className="flex items-center gap-3 p-2 border border-[#2A0F3D] bg-[#0C0612]">
                <div className="flex-1 min-w-0">
                  <p className="text-[#39FF14] font-mono text-sm truncate">{p.name}</p>
                  <p className="text-[#4B6B52] text-xs">${Number(p.price ?? 0).toFixed(2)} ea · in warehouse: {p.stock}</p>
                </div>
                <Input
                  type="number"
                  min="1"
                  max={p.stock}
                  value={r.qty}
                  onChange={(e) => setQty(r.product_id, e.target.value)}
                  className="w-20 bg-[#050003] border-[#39FF14] text-[#39FF14] rounded-none text-center font-mono"
                  data-testid={`kit-row-qty-${r.product_id}`}
                />
                <button
                  onClick={() => removeRow(r.product_id)}
                  className="p-2 text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] border border-[#FF3B30]"
                  data-testid={`kit-row-remove-${r.product_id}`}
                  aria-label="Remove"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Add product picker */}
        {availableToAdd.length > 0 && (
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#39FF14]" />
            <Select onValueChange={addProduct}>
              <SelectTrigger
                className="h-8 bg-[#050003] border-[#39FF14] text-[#39FF14] rounded-none font-mono text-xs flex-1"
                data-testid="kit-add-product"
              >
                <SelectValue placeholder="Add product…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none max-h-64">
                {availableToAdd.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">
                    {p.name} <span className="text-[#6B8E73] text-xs ml-2">${Number(p.price ?? 0).toFixed(2)} · stock {p.stock}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Total */}
        <div className="flex items-center justify-between border-t border-[#39FF14] pt-3">
          <span className="text-[#6B8E73] text-xs uppercase">Kit Value</span>
          <span className="text-[#39FF14] font-mono text-xl font-bold" data-testid="kit-total">
            ${Number(total ?? 0).toFixed(2)}
          </span>
        </div>

        <DialogFooter>
          <Button
            variant="outline" onClick={() => onOpenChange(false)}
            className="border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF3B30] hover:text-[#FF3B30] rounded-none font-mono"
            data-testid="kit-cancel"
          >
            CANCEL
          </Button>
          <Button
            onClick={submit} disabled={saving || rows.length === 0}
            className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono font-bold"
            data-testid="kit-submit"
          >
            {saving ? 'OPENING…' : 'OPEN KIT'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
